import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import AuthBrand from '@/components/AuthBrand';

// Domain validation state interface
interface DomainValidation {
  isValid: boolean;
  isWhitelisted: boolean;
  isChecking: boolean;
  message: string;
  companyName?: string;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoggingIn } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Domain validation state
  const [domainValidation, setDomainValidation] = useState<DomainValidation>({
    isValid: false,
    isWhitelisted: false,
    isChecking: false,
    message: '',
  });

  // Debounced domain validation
  const validateEmailDomain = useCallback(async (email: string) => {
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setDomainValidation({
        isValid: false,
        isWhitelisted: false,
        isChecking: false,
        message: '',
      });
      return;
    }

    setDomainValidation((prev) => ({ ...prev, isChecking: true }));

    try {
      const response = await fetch(
        `/api/auth/validate-email-domain?email=${encodeURIComponent(email)}`
      );
      const result = await response.json();

      setDomainValidation({
        isValid: result.isValid,
        isWhitelisted: result.isWhitelisted,
        isChecking: false,
        message: result.message,
        companyName: result.companyName,
      });
    } catch (error) {
      setDomainValidation({
        isValid: false,
        isWhitelisted: false,
        isChecking: false,
        message: '',
      });
    }
  }, []);

  // Debounce effect for email validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email) {
        validateEmailDomain(email);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [email, validateEmailDomain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await login({ email, password });
      toast({
        title: 'Welcome back!',
        description: 'You have been successfully logged in.',
      });

      const searchParams = new URLSearchParams(window.location.search);
      const returnUrl = searchParams.get('returnUrl');
      const role = (result as any)?.user?.role as string | undefined;
      const adminRedirect =
        role === 'super_admin'
          ? '/super-admin'
          : role === 'hr_admin'
            ? '/hr-panel'
            : role === 'content_admin'
              ? '/content-admin'
              : null;

      // Admins always land on their panel (never onboarding / profile-setup).
      // Non-admins: returnUrl → backend redirectTo → dashboard.
      const redirectTo = adminRedirect || returnUrl || (result as any)?.redirectTo || '/dashboard';

      console.log('🔄 Redirecting to:', redirectTo);

      setTimeout(() => {
        window.location.href = redirectTo;
      }, 100);
    } catch (error: any) {
      console.error('Authentication error:', error);
      if (error.message?.startsWith('EMAIL_NOT_VERIFIED:')) {
        toast({
          variant: 'destructive',
          title: 'Email not verified',
          description: `Please verify your email before logging in.`,
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Login failed',
          description: error.message || 'An error occurred during authentication.',
        });
      }
    }
  };

  const isLoading = isLoggingIn;

  return (
    <div className="min-h-screen bg-hydro-100 flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full max-w-md">
        <AuthBrand subtitle="Stay hydrated, stay well" />

        {/* Login Card */}
        <Card className="bg-card border-lilac-300 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-deep-800 text-center font-kumbh">
              Welcome Back! 👋
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-deep-700 text-sm font-kumbh">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-deep-400 w-4 h-4" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="input-email"
                    className={`pl-10 pr-10 h-12 bg-lilac-50 border-lilac-300 rounded-xl font-kumbh ${
                      domainValidation.isWhitelisted
                        ? 'border-green-500'
                        : domainValidation.message &&
                            !domainValidation.isChecking &&
                            email.length > 0
                          ? 'border-red-500'
                          : ''
                    }`}
                    placeholder="you@company.com"
                  />
                  {/* Domain validation indicator */}
                  {domainValidation.isChecking && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-purple-500 animate-spin" />
                  )}
                  {domainValidation.isWhitelisted && !domainValidation.isChecking && (
                    <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                  {!domainValidation.isWhitelisted &&
                    domainValidation.message &&
                    !domainValidation.isChecking &&
                    email.length > 0 && (
                      <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-red-500" />
                    )}
                </div>
                {/* Domain validation message */}
                {domainValidation.isWhitelisted && (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Email domain approved</span>
                    {domainValidation.companyName && (
                      <span className="text-green-700 font-medium">
                        ({domainValidation.companyName})
                      </span>
                    )}
                  </div>
                )}
                {!domainValidation.isWhitelisted &&
                  domainValidation.message &&
                  !domainValidation.isChecking &&
                  email.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <AlertCircle className="h-3 w-3" />
                      <span>{domainValidation.message}</span>
                    </div>
                  )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-deep-700 text-sm font-kumbh">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-deep-400 w-4 h-4" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="input-password"
                    className="pl-10 pr-10 h-12 bg-lilac-50 border-lilac-300 rounded-xl font-kumbh"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-deep-400 hover:text-deep-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-500 hover:bg-purple-600 text-white h-12 rounded-full font-semibold font-kumbh shadow-lg"
                disabled={isLoading}
                data-testid="button-submit"
              >
                {isLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>

              <div className="text-center">
                <Link href="/forgot-password">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 hover:bg-lilac-100 h-auto p-2 font-kumbh"
                    data-testid="link-forgot-password"
                  >
                    Forgot your password?
                  </Button>
                </Link>
              </div>
            </form>

            <div className="space-y-4 pt-4 border-t border-lilac-200">
              <p className="text-center text-sm text-deep-500 font-kumbh">New to SWYG?</p>
              <Link href="/onboarding">
                <Button
                  variant="outline"
                  className="w-full h-12 bg-lilac-100 border-lilac-300 text-deep-700 hover:bg-lilac-200 rounded-full font-medium font-kumbh"
                  data-testid="link-registration"
                >
                  Create New Account
                </Button>
              </Link>

              <div className="text-center">
                <Link href="/">
                  <button className="text-sm text-deep-400 hover:text-deep-600 transition-colors font-kumbh">
                    ← Back to landing page
                  </button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
